import React from 'react';
import * as dropIn from 'braintree-web-drop-in';
import { usePaymentServices } from '../../common/services';
import { usePaymentConfig } from '../../config/PaymentConfigProvider';
import { useAsync } from '@lialo/common/lib/hooks/useAsync';
import { ErrorType, PaymentError } from '../../common/error';
import { BraintreeService } from './BraintreeService';

interface Config {
  container: React.MutableRefObject<HTMLElement | null>;
  translations: {
    chooseAWayToPay: string;
    chooseAnotherWayToPay: string;
  };
  onPaymentMethodSelected?: (type: 'CreditCard' | 'PayPalAccount') => void;
}

export function usePrepareDropIn(price: string, label: string, config: Config) {
  const { paymentApi } = usePaymentServices();
  const { language, googleMerchantId } = usePaymentConfig();
  const dropInRef = React.useRef<BraintreeService | null>(null);

  const [paymentMethodRequestable, setPaymentMethodRequestable] = React.useState(false);

  const prepareDropIn = React.useCallback(async () => {
    if (!config.container.current) throw new PaymentError(ErrorType.BRAINTREE, 'braintree container must not be null');

    const { clientToken } = await paymentApi.requestClientToken();
    const instance = await dropIn.create({
      locale: language,
      authorization: clientToken,
      container: config.container.current,
      translations: config.translations,
      paymentOptionPriority: ['paypal', 'paypalCredit', 'googlePay', 'applePay', 'card', 'venmo'],
      paypal: {
        flow: 'vault',
        amount: price,
        currency: 'EUR',
      },
      googlePay: {
        googlePayVersion: 2 as any,
        merchantId: googleMerchantId as any,
        transactionInfo: {
          currencyCode: 'EUR',
          totalPrice: price,
          totalPriceStatus: 'FINAL',
        },
      },
      applePay: {
        displayName: 'lialo',
        paymentRequest: {
          total: {
            amount: price,
            label: label,
          },
        } as any,
      },
    });
    dropInRef.current = new BraintreeService(instance);
    return instance;
  }, [paymentApi]);

  const subscribeDropInEvents = React.useCallback((dropIn: dropIn.Dropin) => {
    dropIn.on('paymentMethodRequestable', ({ type }) => {
      config.onPaymentMethodSelected && config.onPaymentMethodSelected(type);
      setPaymentMethodRequestable(true);
    });
    dropIn.on('noPaymentMethodRequestable', () => setPaymentMethodRequestable(false));
    setPaymentMethodRequestable(dropIn.isPaymentMethodRequestable());
  }, []);

  React.useEffect(() => {
    dropInRef.current?.updatePrice(price, label);
  }, [price, label]);

  const { loading, error } = useAsync<dropIn.Dropin, PaymentError>(prepareDropIn, {
    immediate: true,
    onSuccess: subscribeDropInEvents,
  });

  const canSubmit = !loading && (paymentMethodRequestable || Number(price) === 0);

  return [dropInRef, canSubmit, error] as const;
}
